import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { WizardStep } from '../types/Wizard.types';
import { loadWizardState, saveWizardState } from '../utils/orderStorage';

interface UseWizardStepsProps {
  isRerequisition?: boolean;
  requiresPaymentStep: boolean;
  requiresQuestionnaireStep: boolean;
}

interface UseWizardStepsReturn {
  steps: readonly WizardStep[];
  currentStep: number;
  goNext: (isCurrentStepComplete: boolean, setShowErrors: (show: boolean) => void) => void;
  goPrev: () => void;
  animDir: 'next' | 'prev';
  animCount: number;
}

/**
 * Hook that manages wizard step navigation and configuration.
 * Handles dynamic step inclusion based on payment and questionnaire requirements.
 */
export function useWizardSteps({
  isRerequisition = false,
  requiresPaymentStep,
  requiresQuestionnaireStep,
}: UseWizardStepsProps): UseWizardStepsReturn {
  // Dynamic steps based on payment and questionnaire requirements
  const steps = useMemo<WizardStep[]>(() => {
    const allSteps: WizardStep[] = ['address', 'payment', 'questionnaire', 'consents'];
    return allSteps.filter(step => {
      if (step === 'address') return !isRerequisition;
      if (step === 'payment') return requiresPaymentStep;
      if (step === 'questionnaire') return requiresQuestionnaireStep;
      return true;
    });
  }, [isRerequisition, requiresPaymentStep, requiresQuestionnaireStep]);

  const [animDir, setAnimDir] = useState<'next' | 'prev'>('next');
  const [animCount, setAnimCount] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  // Save step position function
  const saveStepPosition = (step: number) => {
    const savedState = loadWizardState();
    saveWizardState({
      currentStep: step,
      // Preserve other fields if they exist, otherwise use defaults
      billingAddress: savedState?.billingAddress,
      consents: savedState?.consents,
      useShippingForBilling: savedState?.useShippingForBilling ?? true,
      promoCode: savedState?.promoCode,
      confirmedBillingAddress: savedState?.confirmedBillingAddress,
    });
  };

  // Create debounced save function (300ms delay)
  const debouncedSaveStepPosition = useMemo(() => debounce(saveStepPosition, 300), []);

  // Load saved step position on initialization (after steps array is determined)
  useEffect(() => {
    const savedState = loadWizardState();
    if (savedState && typeof savedState.currentStep === 'number') {
      // Validate step index is within bounds of current steps array
      // This handles cases where step array changes (e.g., payment step added/removed)
      const savedStep = savedState.currentStep;
      if (savedStep >= 0 && savedStep < steps.length) {
        setCurrentStep(savedStep);
      }
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Removing a product can drop the questionnaire step, which would otherwise
  // leave currentStep pointing past the end of the steps array.
  useEffect(() => {
    setCurrentStep(step => Math.min(step, Math.max(steps.length - 1, 0)));
  }, [steps.length]);

  // Track if step was changed via goNext/goPrev (to avoid double-saving)
  const stepChangedViaNavigationRef = useRef(false);

  // Save step position whenever it changes (debounced, unless changed via navigation)
  useEffect(() => {
    if (stepChangedViaNavigationRef.current) {
      // Step was changed via navigation, which already saved immediately
      // Cancel any pending debounced saves and reset the flag
      debouncedSaveStepPosition.cancel();
      stepChangedViaNavigationRef.current = false;
    } else {
      // Step changed via other means (e.g., restored from storage), use debounced save
      debouncedSaveStepPosition(currentStep);
    }
  }, [currentStep, debouncedSaveStepPosition]);

  // Flush debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSaveStepPosition.flush();
      debouncedSaveStepPosition.cancel();
    };
  }, [debouncedSaveStepPosition]);

  const goNext = (isCurrentStepComplete: boolean, setShowErrors: (show: boolean) => void) => {
    if (!isCurrentStepComplete) {
      setShowErrors(true);
      return;
    }
    setAnimDir('next');
    // Mark that step is changing via navigation (prevents double-save in useEffect)
    stepChangedViaNavigationRef.current = true;
    setCurrentStep(s => {
      const nextStep = Math.min(s + 1, steps.length - 1);
      // Save immediately on step change (bypass debounce)
      saveStepPosition(nextStep);
      return nextStep;
    });
    setAnimCount(c => c + 1);
  };

  const goPrev = () => {
    setAnimDir('prev');
    // Mark that step is changing via navigation (prevents double-save in useEffect)
    stepChangedViaNavigationRef.current = true;
    setCurrentStep(s => {
      const prevStep = Math.max(s - 1, 0);
      // Save immediately on step change (bypass debounce)
      saveStepPosition(prevStep);
      return prevStep;
    });
    setAnimCount(c => c + 1);
  };

  return {
    steps,
    currentStep,
    goNext,
    goPrev,
    animDir,
    animCount,
  };
}
